/* eslint-disable @typescript-eslint/no-unused-vars */
import { InputWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { matchSorter } from 'match-sorter'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  buildFeatureSet,
  getCredentials,
  hasRequiredFeatures
} from '../../cache/auth'
import SectionTitle from '../../components/SectionTitle'
import { ACCOMMODATION_FACILITIES_QUERY } from '../../graphql/accommodationFacilities'
import { MODIFY_ACCOMMODATION_FACILITIES_MUTATION } from '../../graphql/modifyAccommodationFacilities'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import accommodationPagesRoleManagement from '../../staticRoleManagement/accommodationsRoleManagement'
import { FacilityGroup } from '../../types/accommodationFacilities'
import FacilitiesCategory from './FacilitiesCategory'
import FacilitiesCategorySkeleton from './FacilitiesCategory.skeleton'
import useStyles from './styles/index.styles'

const AccommodationFacilities = () => {
  const theme = useTheme()
  const classes = useStyles()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [load, setLoad] = useState(false)

  const [filteredAccommodationFacilities, setFilteredAccommodationFacilities] =
    useState<FacilityGroup[]>([])
  const [selectedIDs, setSelectedIDs] = useState<string[]>([])

  const [searchInputValue, setSearchInputValue] = useState<string>('')

  const params = useParams<{ accID: string; propertyCode: string }>()

  const { data, loading } = useQuery<{
    getAccommodationFacilities: FacilityGroup[]
  }>(ACCOMMODATION_FACILITIES_QUERY, {
    variables: {
      propertyCode: params.propertyCode
    },
    // Without this a failed lookup left `data` undefined and the tab rendered an empty
    // facilities list with no message — indistinguishable from an accommodation that
    // genuinely has none. Reported as "there is no facilities list to choose from".
    //
    // A FIXED message, not `err.message`. The operator needs to know the lookup failed;
    // they do not need the upstream text, and here that text is unsanitized all the way
    // down: asset's repository returns the raw Mongo driver error from FindOne/UpdateOne,
    // accommodation_rpc.go returns it without status.Error wrapping, and the BFF resolver
    // (accommodation_facilities.resolvers.go) does `return nil, err` rather than routing
    // through helpers.PromotionGraphQLErrorFromGRPC the way the promotion path does. So a
    // driver-level failure — an E11000 naming the collection, index and key, a timeout
    // naming a host — would render verbatim in a toast for any operator who can open this
    // tab. Traced repo → rpc → resolver on 2026-09-05; not reproduced against a live
    // stack, so the leak is established by reading the chain, not by triggering it.
    //
    // The real error still goes to the console, which is where a developer diagnosing
    // this is already looking, and matches what the Apollo link does for network errors.
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('[facilities] lookup failed', err)
      toast.error(getTranslate('Could not load the facilities list.'))
    }
  })

  const [mutateFunction, { loading: loadingForMutationFacilites }] =
    useMutation(MODIFY_ACCOMMODATION_FACILITIES_MUTATION, {
      notifyOnNetworkStatusChange: true
    })

  const onSave = () => {
    setLoad(true)
    mutateFunction({
      variables: {
        selectedIDs: selectedIDs,
        propertyCode: params.propertyCode
      }
    })
      .then(({ data: response }) => {
        setLoad(false)
        toast.success(response.saveAccommodationFacilities.message)
      })
      .catch((error) => {
        // The empty `onError: () => {}` that used to sit on this mutation made Apollo
        // RESOLVE on failure instead of rejecting, so this .then ran with `data`
        // undefined, `response.saveAccommodationFacilities` threw a TypeError, and the
        // operator was shown "Cannot read properties of undefined" as the save result.
        // Dropping it lets the real ApolloError reach here.
        //
        // Fixed message for the same reason as the query above — SaveAccommodationFacilities
        // carries the raw Mongo error out through an unwrapped rpc and an unsanitized
        // resolver, and a duplicate-key error names the collection and index.
        setLoad(false)
        // eslint-disable-next-line no-console
        console.error('[facilities] save failed', error)
        toast.error(getTranslate('Could not save the facilities.'))
      })
  }

  // the first assignment of accommodationFacilities when data is loaded
  useEffect(() => {
    if (data)
      setFilteredAccommodationFacilities(data?.getAccommodationFacilities)
  }, [data])

  // --------- when searchInput changes ------------
  useEffect(() => {
    if (data) {
      let newAccommodationFacilities = data.getAccommodationFacilities

      if (searchInputValue) {
        newAccommodationFacilities = newAccommodationFacilities
          .map((facilityGroup) => ({
            label: facilityGroup.label,
            facilities: matchSorter(
              facilityGroup.facilities,
              searchInputValue,
              {
                keys: ['label']
              }
            )
          }))
          .filter((facilityGroup) => facilityGroup.facilities.length > 0)
      }

      setFilteredAccommodationFacilities([...newAccommodationFacilities])
    }
  }, [searchInputValue])
  // --------- end when searchInput changes ------------

  const credentials = getCredentials()
  const featureSet = buildFeatureSet(credentials)

  const hasAccessToFacilities = hasRequiredFeatures(
    accommodationPagesRoleManagement
      .find(
        (item) => item.route === '/accommodations/:accID/:propertyCode/:page'
      )
      ?.tabs?.find((tabItem) => tabItem.params.page === 'facilities')
      ?.requiredFeatures,
    featureSet
  )

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {isSmallScreen ? (
          <SectionTitle title="Facilities" marginBottom />
        ) : (
          <SectionTitle title="Accommodation Facilities" marginBottom />
        )}
      </div>
      <div className={classes.searchInputContainer}>
        <InputWithLabel
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.currentTarget.value)}
          placeholder={getTranslate('Search by name')}
          autoComplete="off"
          noHelperText
          fullWidth
          startIcon={<SearchIcon />}
          className={classes.searchInput}
        />
      </div>
      <div className={classes.facilitiesListContainer}>
        {loading &&
          [...Array(3)].map((_, index) => (
            <FacilitiesCategorySkeleton key={index.toString()} />
          ))}
        {!loading &&
          filteredAccommodationFacilities.map(
            (facilityGroupItem, facilityGroupItemIndex) => (
              <FacilitiesCategory
                key={facilityGroupItem.label}
                title={facilityGroupItem.label}
                facilities={facilityGroupItem.facilities}
                selectedIDs={selectedIDs}
                setSelectedIDs={setSelectedIDs}
              />
            )
          )}
      </div>
      <div className={classes.button}>
        <Button
          disabled={
            load || !hasAccessToFacilities || loadingForMutationFacilites
          }
          data-cy="saveFacilities"
          variant="contained"
          color="primary"
          fullWidth={isSmallScreen}
          onClick={onSave}>
          {getTranslate('Save')}
          {loadingForMutationFacilites && (
            <CircularProgress
              className={classes.circularProgress}
              size={16}
              color="primary"
            />
          )}
        </Button>
      </div>
    </div>
  )
}

export default AccommodationFacilities
